import { useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { injectIntl } from 'react-intl';
import axios from 'axios';
import RadioGroup from '../form/RadioGroup';
import BottomBar from '../BottomBar';
import ClinicDialog from './clinicDialog';
import usePersistedDraft from '../hooks/usePersistedDraft';
import { API_URL } from '../../../environment';
import { headers } from '../../../actions/defaults';

const PracticeDetails0 = ({
  openLoadingPanel,
  closeLoadingPanel,
  nextPage,
  currentStep,
  intl,
  currentSubStep,
  previousPage,
  bottomBar,
  skipToEnd,
  isClinicCounsellor,
  setIsClinicCounsellor,
  specialized,
  draftValues,
  onDraftChange,
}) => {
  const [open, setOpen] = useState(false);
  const [clinicProfile, setClinicProfile] = useState(null);

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: draftValues || {
      clinic_yn: null,
    },
  });

  usePersistedDraft(getValues, onDraftChange);

  const onSubmit = async values => {
    const clinicYN = values.clinic_yn;

    try {
      const response = await axios.get(
        `${API_URL}/practice/users/me`,
        headers(),
      );
      const resClinicProfile =
        response?.data?.clinic_counsellor_profile || null;

      if (resClinicProfile && resClinicProfile?.clinic_name) {
        setClinicProfile(resClinicProfile);
      }

      if (clinicYN === 'false' && resClinicProfile === null) {
        nextPage();
      } else {
        setOpen(true);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error fetching clinic profile:', err);
    }
  };

  const SpecializedBottomBar = bottomBar;

  return (
    <form id="practice_detail" onSubmit={handleSubmit(onSubmit)}>
      <div className="page-title">
        <h1>{intl.formatMessage({ defaultMessage: 'Practice Details' })}</h1>
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        <div className="sub-page-indicator">{`${currentSubStep + 1}/7`}</div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>
          {intl.formatMessage({
            defaultMessage: 'Are you registering this account under a clinic?',
          })}
        </h3>
      </div>

      <div className="input-row">
        <Controller
          name="clinic_yn"
          control={control}
          rules={{
            required: intl.formatMessage({ defaultMessage: 'Required' }),
          }}
          render={({ field }) => (
            <RadioGroup
              {...field}
              options={[
                {
                  label: intl.formatMessage({
                    defaultMessage:
                      'Yes, I am registering as a practitioner under a licensed clinic',
                  }),
                  value: 'true',
                },
                {
                  label: intl.formatMessage({
                    defaultMessage:
                      'No, I am registering as an individual practitioner',
                  }),
                  value: 'false',
                },
              ]}
              error={errors.clinic_yn?.message}
            />
          )}
        />
      </div>

      {open && (
        <ClinicDialog
          nextPage={nextPage}
          open={open}
          setOpen={setOpen}
          clinicProfile={clinicProfile}
          setIsClinicCounsellor={setIsClinicCounsellor}
          clinicYN={watch('clinic_yn')}
        />
      )}

      {bottomBar ? (
        <SpecializedBottomBar
          openLoadingPanel={openLoadingPanel}
          closeLoadingPanel={closeLoadingPanel}
          currentStep={currentStep}
          intl={intl}
          previousPage={previousPage}
          skipToEnd={skipToEnd}
          isClinicCounsellor={isClinicCounsellor}
        />
      ) : (
        <BottomBar
          openLoadingPanel={openLoadingPanel}
          closeLoadingPanel={closeLoadingPanel}
          currentStep={currentStep}
          intl={intl}
          previousPage={previousPage}
          specialized={specialized}
        />
      )}
    </form>
  );
};

PracticeDetails0.propTypes = {
  openLoadingPanel: PropTypes.func.isRequired,
  closeLoadingPanel: PropTypes.func.isRequired,
  nextPage: PropTypes.func.isRequired,
  skipToEnd: PropTypes.func,
  currentStep: PropTypes.number.isRequired,
  currentSubStep: PropTypes.number,
  intl: PropTypes.object.isRequired,
  previousPage: PropTypes.func,
  bottomBar: PropTypes.func,
  setIsClinicCounsellor: PropTypes.func,
  isClinicCounsellor: PropTypes.bool,
  draftValues: PropTypes.object,
  onDraftChange: PropTypes.func,
  specialized: PropTypes.bool,
};

export default injectIntl(PracticeDetails0);
